<script setup lang="ts">
import { computed, ref, toRefs, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '@/api';
import { DgrIcon, DgrPopover, IconTypes } from '@stockmarkteam/donguri-ui';
import { ThemeList } from '@/types';
import { themeEditable } from '@/utils/authorization';
import { useGroups, useUserInfo } from '@/utils/swr';
import { useEmitter, useStore } from '@/utils/vue';
import { useSnackbar } from '../snackbar/use-snackbar';

type Props = {
  theme: ThemeList;
};

const props = defineProps<Props>();
const emit = defineEmits<{
  'visibility-change': [isVisible: boolean];
  'theme-delete': [themeId: number];
}>();

const { theme } = toRefs(props);

const emitter = useEmitter();
const router = useRouter();
const route = useRoute();
const store = useStore();

const { data: userInfo } = useUserInfo();
const { data: groups } = useGroups();
const { createSnackbar } = useSnackbar();

const contentRef = ref<HTMLElement | null>(null);
watch(contentRef, newContentRef => emit('visibility-change', !!newContentRef));

const dropdownZIndex = Number(
  // テーマヘッダーよりも上に表示する
  getComputedStyle(document.body).getPropertyValue('--z-content-header') + 1,
);

const themeGroup = computed(() =>
  groups.value?.groups.find(g => g.id === theme.value.group_id),
);

const hasPermission = computed(() =>
  themeEditable(userInfo.value, theme.value, themeGroup.value),
);

const copyTheme = async () => {
  await api.trackThemeEvent('to_create_theme', {
    pageName: route.meta.type === 'theme' ? 'theme' : 'theme_search',
    pageUrl: route.fullPath,
    from: 'theme_copy',
    source_theme_id: theme.value.id,
  });
  router.push({
    name: 'themeCreate',
    query: {
      copyTheme: theme.value.id,
    },
  });
};

const deleteTheme = () => {
  if (!hasPermission.value) {
    return;
  }
  const deleteAction = async () => {
    try {
      await api.deleteTheme(theme.value.id);
      await store.dispatch('themeList/fetchThemeList');
      emitter.emit('theme-updated', {});
      emit('theme-delete', theme.value.id);

      const isCurrentPageThemeDetail = route.meta.type === 'theme';
      if (isCurrentPageThemeDetail) {
        router.push({ name: 'anewsHome' });
      }
      createSnackbar({
        message: 'テーマを削除しました',
        type: 'success',
      });
    } catch (err) {
      createSnackbar({
        message: 'テーマを削除できませんでした',
        type: 'error',
      });
      throw err;
    }
  };
  const payload = {
    headerText: 'テーマを削除しますか？',
    bodyText:
      '削除したテーマは元に戻すことはできません。\n本当にテーマを削除しますか？',
    action: deleteAction,
  };
  store.commit('confirmationModal/showConfirmationModal');
  store.commit('confirmationModal/setTextAndAction', payload);
};

type Action = {
  name: string;
  func: () => Promise<void>;
  icon: IconTypes;
};

const actions = computed(() => {
  const actionList: Action[] = [
    {
      name: '編集',
      func: async () => {
        router.push({
          name: 'themeEdit',
          params: { themeId: theme.value.id },
        });
      },
      icon: 'pencil',
    },
    {
      name: 'コピーして作成',
      func: async () => await copyTheme(),
      icon: 'copy',
    },
  ];
  if (hasPermission.value) {
    actionList.push({
      name: '削除',
      func: async () => await deleteTheme(),
      icon: 'trash',
    });
  }
  return actionList;
});
</script>

<template>
  <DgrPopover :z-index="dropdownZIndex">
    <template #default="{ triggerProps }">
      <button
        v-bind="triggerProps"
        type="button"
        class="dropdown-btn c-btn c-btn--small c-btn--auto"
      >
        <DgrIcon name="ellipsis-h" size="small" />
      </button>
    </template>
    <template #content="{ close }">
      <div ref="contentRef" class="item-container">
        <template v-for="action in actions" :key="action.name">
          <button
            class="item c-text-regular--m"
            @click="
              action.func();
              close();
            "
          >
            <DgrIcon :name="action.icon" size="small" class="icon" />{{
              action.name
            }}
          </button>
          <div
            class="separator"
            v-if="action !== actions[actions.length - 1]"
          ></div>
        </template>
      </div>
    </template>
  </DgrPopover>
</template>

<style lang="scss" scoped>
.dropdown-btn {
  border: none;
  padding: 0 8px;
}
.item-container {
  padding: 12px 0;

  .item {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    height: 30px;
    padding: 4px 12px;
    border: none;
    border-radius: 0;

    background: none;
    &:hover {
      background: $color-gray400;
    }
    .icon {
      margin-right: 4px;
    }
  }
  .separator {
    margin: 8px 0;
    border-top: 1px solid $color-gray400;
  }
}
</style>
